import React, { FC } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { SwiperOptions } from 'swiper';
import dayjs from 'dayjs';
import multiTransactionIcon from '@assets/icons/multi-transaction.svg';
import arrowUp from '@assets/icons/arrow-up.svg';
import VaultStatsTile from './VaultStatsTile/VaultStatsTile';

import bgParticles1 from './bg-stats-tiles-particles-1.png';
import bgParticles2 from './bg-stats-tiles-particles-2.png';
import bgParticles3 from './bg-stats-tiles-particles-3.png';

import Button from '../common/Button/Button';

import { RoutingPaths } from '../../paths';
import { useVaultsTiles } from './hooks/useVaultsTiles';
import { useUserContext } from '../../contexts/UserContext';

import styles from './VaultStatsTiles.module.scss';

interface Props {
  account: string;
  addyData: any;
  vaults: any;
}

const breakpoints: {
  [width: number]: SwiperOptions;
  [ratio: string]: SwiperOptions;
} = {
  768: {
    slidesPerView: 3,
    spaceBetween: 22,
    enabled: false,
  },
  1024: {
    slidesPerView: 3,
    spaceBetween: 32,
    enabled: false,
  },
  1200: {
    slidesPerView: 3,
    spaceBetween: 64,
    enabled: false,
  },
};

const VaultStatsTiles: FC<Props> = ({ account, addyData, vaults }) => {
  const {
    highestBoost,
    lockedAmount,
    unlockTimestamp,
    vaultsWithInvests,
    investedInVaults,
    pendingVaultsReward,
    collectRewards,
  } = useVaultsTiles(account, addyData, vaults);
  const { isWalletConnected } = useUserContext();

  const unlockDaysLeft = dayjs(unlockTimestamp).diff(dayjs(), 'days');

  return (
    <div className={styles.tiles}>
      <Swiper
        enabled
        centeredSlides
        centeredSlidesBounds
        slidesPerView={1.4}
        spaceBetween={22}
        breakpoints={breakpoints}
        className={styles.swiperContainer}
      >
        <SwiperSlide className={styles.swiperSlide}>
          <VaultStatsTile
            isActive={isWalletConnected}
            title="My Vault Investments"
            inactiveDesc="to see a summary of your investments"
            value={`$${investedInVaults}`}
            activeDesc={vaultsWithInvests.length > 0 ? `in ${vaultsWithInvests.length} Vaults` : ''}
            bgParticles={bgParticles1}
          />
        </SwiperSlide>

        <SwiperSlide className={styles.swiperSlide}>
          <VaultStatsTile
            isActive={isWalletConnected}
            title="My Boost"
            inactiveDesc="to see your boost"
            activeDesc={`${lockedAmount} ADDY locked for ${unlockDaysLeft} ${
              unlockDaysLeft === 1 ? 'day' : 'days'
            }`}
            valuePrefix="up to"
            value={`${highestBoost}x`}
            bgParticles={bgParticles2}
            buttonElement={
              <Button
                color="primary"
                to={RoutingPaths.BOOST}
                className={styles.button}
                startEnhancer={<img src={arrowUp} alt="arrow up" />}
              >
                Increase
              </Button>
            }
          />
        </SwiperSlide>

        <SwiperSlide className={styles.swiperSlide}>
          <VaultStatsTile
            isActive={isWalletConnected}
            title="My rewards"
            value={`${pendingVaultsReward}`}
            inactiveDesc="to see your rewards"
            valueSuffix="ADDY"
            bgParticles={bgParticles3}
            buttonElement={
              <Button
                color="primary"
                onClick={collectRewards}
                className={styles.button}
                startEnhancer={<img src={multiTransactionIcon} alt="multi transaction" />}
              >
                Claim & Vest All
              </Button>
            }
          />
        </SwiperSlide>
      </Swiper>
    </div>
  );
};

export default VaultStatsTiles;
